import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { forwardRef, useEffect } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { Icon } from '../../icons/icon';
import { Icons } from '../../icons/iconNames';
import { Badge, BadgeSize } from '../badge/Badge';

import styles from './IconButton.module.css';

export enum IconButtonSize {
  TINY = 'tiny',
  SMALL = 'small',
  REGULAR = 'regular',
  LARGE = 'large',
}
interface IconButtonCoreProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onClick'> {
  active?: boolean;
  autoCall?: boolean;
  ghost?: boolean;
  light?: boolean;
  onClick?: (event?: MouseEvent<HTMLButtonElement>) => void;
  badge?: string | number | boolean;
  badgeSize?: BadgeSize.Tiny | BadgeSize.Small;
  size?: IconButtonSize;
  squared?: boolean;
  timer?: number;
  timerIncreasing?: boolean;
  timerReversed?: boolean;
}

interface IconButtonIconProps extends IconButtonCoreProps {
  icon: keyof Icons;
  children?: never;
}

interface IconButtonChildrenProps extends IconButtonCoreProps {
  children: ReactNode;
  icon?: never;
}

export type IconButtonProps = IconButtonIconProps | IconButtonChildrenProps;

const IconButton = forwardRef<HTMLSpanElement, IconButtonProps>((props, ref): JSX.Element => {
  const {
    active,
    autoCall = true,
    children,
    className = '',
    disabled,
    ghost,
    icon,
    light,
    onClick,
    badge,
    badgeSize = BadgeSize.Tiny,
    size = IconButtonSize.REGULAR,
    squared,
    timer,
    timerIncreasing,
    timerReversed,
    ...rest
  } = props;

  const audioTrigger = useAudioTrigger();

  const classMods: string = useModifiers(
    styles,
    'iconButton',
    {
      active,
      disabled,
      ghost,
      light,
      squared,
      timerIncreasing,
      timerReversed,
      smallBadge: badgeSize === BadgeSize.Small,
      [size as string]: true,
    },
    { withBaseClass: true, extraClassName: className }
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    audioTrigger.play({ name: 'menuClick' });
    onClick?.(event);
  };

  useEffect(() => {
    if (timer && onClick && autoCall) {
      const timerId = setTimeout(() => onClick(), timer);
      return (): void => clearTimeout(timerId);
    }
  }, [timer, onClick, autoCall]);

  return (
    <div className={styles.iconButton__wrapper}>
      <button
        type="button"
        data-testid="icon-button"
        className={classMods}
        disabled={disabled}
        onClick={handleClick}
        {...rest}
      >
        {/*inner wrapper fixes button scaling on active state issue*/}
        <span className={styles.iconButton__innerWrapper} ref={ref}>
          <span className={styles.iconButton__content}>{icon ? <Icon icon={icon} /> : children}</span>
          {renderTimer(timer)}
        </span>
        {renderBadge(badge, badgeSize)}
      </button>
    </div>
  );
});

const renderTimer = (timer: number | undefined): JSX.Element | null => {
  if (!timer) return null;
  return (
    <svg className={styles.iconButton__timer}>
      <circle className={styles.iconButton__timerBackground} />
      <circle className={styles.iconButton__timerPath} style={{ animationDuration: `${timer}ms` }} />
    </svg>
  );
};

const renderBadge = (badge: string | number | boolean | undefined, size: BadgeSize): JSX.Element | null => {
  if (!badge) return null;
  return (
    <Badge
      className={styles.badge}
      size={size}
      value={typeof badge !== 'boolean' ? badge : undefined}
      grow="start"
      impact
    />
  );
};

IconButton.displayName = 'IconButton';

export { IconButton, Icons };
